import {
  Box,
  ButtonBase,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { MouseEvent as ReactMouseEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EDITOR_PICKER_POPOVER_Z_INDEX } from "../components/editorPickerPopoverZIndex.js";
import type { BehaviorTemplateCatalogItem } from "./BehaviorTemplateCatalog.js";
import { matchesBehaviorCatalogSearch } from "./behaviorCatalogSearch.js";

export interface PortConnectionPickerItem extends BehaviorTemplateCatalogItem {
  targetPortId?: string;
}

export interface PortConnectionPickerState {
  mouseX: number;
  mouseY: number;
}

export interface PortConnectionPickerProps {
  menuState: PortConnectionPickerState | null;
  title: string;
  searchPlaceholder: string;
  emptyLabel: string;
  items: PortConnectionPickerItem[];
  onClose: () => void;
  onSelect: (item: PortConnectionPickerItem) => void;
}

type GroupedItems = {
  key: string;
  categoryLabel: string;
  subCategoryLabel?: string;
  items: PortConnectionPickerItem[];
};

/**
 * Searchable contextual picker used when a dragged connection is released in
 * empty space and the user wants to insert a new compatible node directly.
 */
export const PortConnectionPicker = ({
  menuState,
  title,
  searchPlaceholder,
  emptyLabel,
  items,
  onClose,
  onSelect,
}: PortConnectionPickerProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const wasOpenRef = useRef(false);
  const [stableItems, setStableItems] =
    useState<PortConnectionPickerItem[]>(items);
  const isOpen = menuState != null;

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) {
      return;
    }
    setSearchQuery("");
    setStableItems(items);
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, items]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setStableItems(items);
  }, [isOpen, items]);

  const handleClose = () => {
    const activeElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    if (activeElement && paperRef.current?.contains(activeElement)) {
      activeElement.blur();
    }
    onClose();
  };

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
    if (!normalizedSearch) {
      return stableItems;
    }
    return stableItems.filter((item) =>
      matchesBehaviorCatalogSearch(item, normalizedSearch),
    );
  }, [searchQuery, stableItems]);

  const groupedItems = useMemo<GroupedItems[]>(() => {
    const groups = new Map<string, GroupedItems>();
    filteredItems.forEach((item) => {
      const key = `${item.categoryLabel}::${item.subCategoryLabel ?? ""}`;
      const existing = groups.get(key);
      if (existing) {
        existing.items.push(item);
        return;
      }
      groups.set(key, {
        key,
        categoryLabel: item.categoryLabel,
        subCategoryLabel: item.subCategoryLabel,
        items: [item],
      });
    });
    return Array.from(groups.values());
  }, [filteredItems]);

  const handleSelectItem = (
    event: ReactMouseEvent<HTMLButtonElement>,
    item: PortConnectionPickerItem,
  ) => {
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur();
    }
    onSelect(item);
  };

  return (
    <Popover
      open={isOpen}
      onClose={handleClose}
      disableRestoreFocus
      anchorReference="anchorPosition"
      anchorPosition={
        menuState
          ? { top: menuState.mouseY, left: menuState.mouseX }
          : undefined
      }
      PaperProps={{
        ref: paperRef,
        "data-port-connection-picker": "true",
        sx: {
          mt: 1,
          width: 360,
          maxHeight: 420,
          overflow: "hidden",
          zIndex: EDITOR_PICKER_POPOVER_Z_INDEX,
        },
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <TextField
          inputRef={inputRef}
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={searchPlaceholder}
          sx={{ mb: 1 }}
        />

        {groupedItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            {emptyLabel}
          </Typography>
        ) : (
          <Stack
            spacing={1}
            sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}
          >
            {groupedItems.map((group) => (
              <Box key={group.key}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", fontWeight: 600, mb: 0.5, px: 0.5 }}
                >
                  {group.subCategoryLabel
                    ? `${group.categoryLabel} / ${group.subCategoryLabel}`
                    : group.categoryLabel}
                </Typography>
                <Stack spacing={0.5}>
                  {group.items.map((item) => (
                    <ButtonBase
                      key={item.id}
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={(event) => handleSelectItem(event, item)}
                      sx={{
                        width: "100%",
                        justifyContent: "flex-start",
                        borderRadius: 1,
                        px: 1,
                        py: 0.75,
                        textAlign: "left",
                        border: "1px solid transparent",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          backgroundColor: item.dotColor,
                          mr: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">{item.label}</Typography>
                    </ButtonBase>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Popover>
  );
};
